import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Collapse,
  alpha,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReactMarkdown from 'react-markdown';
import MarkdownGuide from './MarkdownGuide';

function NoteModal({ open, onClose, onSave, categories, note }) {
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [preview, setPreview] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (note) {
      setContent(note.content);
      setCategoryId(note.categoryId);
    } else {
      setContent('');
      setCategoryId('');
    }
    setPreview(false);
  }, [note, open]);

  const handleSave = () => {
    if (!content.trim() || !categoryId) return;
    onSave({ content, categoryId });
    setContent('');
    setCategoryId('');
  };

  const selectedCategory = categories.find(cat => cat.id === categoryId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{note ? 'Edit Note' : 'New Note'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            select
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {categories.map((category) => (
              <MenuItem 
                key={category.id} 
                value={category.id}
                sx={{
                  borderLeft: 3,
                  borderColor: category.color,
                  pl: 2,
                  '&:hover': {
                    backgroundColor: alpha(category.color, 0.08),
                  },
                  '&.Mui-selected': {
                    backgroundColor: alpha(category.color, 0.12),
                    '&:hover': {
                      backgroundColor: alpha(category.color, 0.16),
                    },
                  },
                }}
              >
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Markdown supported
            </Typography>
            <IconButton size="small" onClick={() => setShowGuide(!showGuide)}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <Collapse in={showGuide}>
            <MarkdownGuide />
          </Collapse>

          {!preview ? (
            <TextField
              multiline
              rows={8}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              placeholder="Write your note here..."
              sx={{
                '& .MuiOutlinedInput-root': selectedCategory && {
                  '&.Mui-focused': {
                    '& fieldset': {
                      borderColor: selectedCategory.color,
                    },
                  },
                },
              }}
            />
          ) : (
            <Box sx={{ 
              p: 2, 
              border: 1, 
              borderColor: selectedCategory ? alpha(selectedCategory.color, 0.3) : 'grey.300',
              borderRadius: 1, 
              minHeight: 200,
              backgroundColor: selectedCategory ? alpha(selectedCategory.color, 0.02) : 'transparent',
            }}>
              <ReactMarkdown>{content}</ReactMarkdown>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreview(!preview)} color="inherit">
          {preview ? 'Edit' : 'Preview'}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={!content.trim() || !categoryId}
          sx={selectedCategory && {
            backgroundColor: selectedCategory.color,
            '&:hover': {
              backgroundColor: alpha(selectedCategory.color, 0.8),
            },
          }}
        >
          {note ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NoteModal;